import React from 'react';
import { Box, Typography, Button } from '@mui/material';

const THEMES = {
  dark:   { name:'🌑 Dark',   menuBg:'#1a1a2e', menuText:'#ffffff', cardOpacity:1.0 },
  medium: { name:'🌓 Medium', menuBg:'#1e2a3a', menuText:'#e0e0e0', cardOpacity:0.85 },
  glass:  { name:'🪟 Glass',  menuBg:'rgba(26,26,46,0.5)', menuText:'#ffffff', cardOpacity:0.5 },
  light:  { name:'☀️ Light',  menuBg:'#e8eaf6', menuText:'#1a1a2e', cardOpacity:0.95 },
  solid:  { name:'⬛ Solid',  menuBg:'#000000', menuText:'var(--ac1, #7c4dff)', cardOpacity:1.0 },
};

const DEFAULT = { menuBg:'#1a1a2e', menuText:'#ffffff', cardOpacity:1.0, menuOpacity:1.0, tableOpacity:1.0, chatOpacity:1.0 };

function dispatchAll() { ['theme_changed','card_opacity_changed','menu_opacity_changed'].forEach(e => window.dispatchEvent(new Event(e))); }

export default function ThemeSettings() {
  const [menuBg, setMenuBg] = React.useState(() => localStorage.getItem('menu_bg') || '#1a1a2e');
  const [menuText, setMenuText] = React.useState(() => localStorage.getItem('menu_text') || '#ffffff');
  const [cardOp, setCardOp] = React.useState(() => parseFloat(localStorage.getItem('card_opacity') || '1'));
  const [menuOp, setMenuOp] = React.useState(() => parseFloat(localStorage.getItem('menu_opacity') || '1'));
  const [tableOp, setTableOp] = React.useState(() => parseFloat(localStorage.getItem('table_opacity') || '1'));
  const [chatOp, setChatOp] = React.useState(() => parseFloat(localStorage.getItem('chat_opacity') || '1'));
  const [activeTheme, setActiveTheme] = React.useState(() => localStorage.getItem('active_theme') || '');

  const applyAll = (bg, text, co, mo, to, cho) => {
    localStorage.setItem('menu_bg', bg); localStorage.setItem('menu_text', text);
    localStorage.setItem('card_opacity', co); localStorage.setItem('menu_opacity', mo);
    localStorage.setItem('table_opacity', to); localStorage.setItem('chat_opacity', cho);
    document.documentElement.style.setProperty('--card-opacity', co);
    document.documentElement.style.setProperty('--table-opacity', to);
    document.documentElement.style.setProperty('--chat-opacity', cho);
    dispatchAll();
  };

  const applyTheme = (key, t) => {
    setMenuBg(t.menuBg); setMenuText(t.menuText);
    setCardOp(t.cardOpacity); setMenuOp(t.cardOpacity);
    setTableOp(t.cardOpacity); setChatOp(t.cardOpacity);
    setActiveTheme(key); localStorage.setItem('active_theme', key);
    applyAll(t.menuBg, t.menuText, t.cardOpacity, t.cardOpacity, t.cardOpacity, t.cardOpacity);
  };

  const handleApply = () => { applyAll(menuBg, menuText, cardOp, menuOp, tableOp, chatOp); };

  const handleReset = () => {
    setMenuBg(DEFAULT.menuBg); setMenuText(DEFAULT.menuText);
    setCardOp(DEFAULT.cardOpacity); setMenuOp(DEFAULT.menuOpacity);
    setTableOp(DEFAULT.tableOpacity); setChatOp(DEFAULT.chatOpacity);
    setActiveTheme(''); localStorage.setItem('active_theme','');
    applyAll(DEFAULT.menuBg, DEFAULT.menuText, DEFAULT.cardOpacity, DEFAULT.menuOpacity, DEFAULT.tableOpacity, DEFAULT.chatOpacity);
  };

  const sliders = [
    { label:'Картки/Paper', val:cardOp, fn:(v)=>setCardOp(v) },
    { label:'Меню (бокова панель)', val:menuOp, fn:(v)=>setMenuOp(v) },
    { label:'Таблиці', val:tableOp, fn:(v)=>setTableOp(v) },
    { label:'Чат', val:chatOp, fn:(v)=>setChatOp(v) },
  ];

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ color:'#aaa', mb:1 }}>🎨 Готові теми</Typography>
      <Box sx={{ display:'flex', gap:1, flexWrap:'wrap', mb:2 }}>
        {Object.entries(THEMES).map(([k,t]) => (
          <Button key={k} size="small" variant={activeTheme===k?'contained':'outlined'} onClick={() => applyTheme(k,t)}
            sx={{ fontSize:11, bgcolor:activeTheme===k?'var(--ac1, #7c4dff)':'transparent', borderColor:activeTheme===k?'var(--ac1, #7c4dff)':'#555', color:activeTheme===k?'#fff':'#ccc' }}>{t.name}</Button>
        ))}
      </Box>

      <Typography variant="subtitle2" sx={{ color:'#aaa', mb:1 }}>🗂 Колір навігації</Typography>
      <Box sx={{ display:'flex', gap:3, mb:2 }}>
        <Box>
          <Typography variant="caption" sx={{ color:'#888', display:'block', mb:0.5 }}>Фон меню</Typography>
          <input type="color" value={menuBg.startsWith('rgba')?'#1a1a2e':menuBg} onChange={e=>setMenuBg(e.target.value)} style={{ width:48, height:36, cursor:'pointer', border:'1px solid #555', borderRadius:4, background:'none' }}/>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color:'#888', display:'block', mb:0.5 }}>Текст меню</Typography>
          <input type="color" value={menuText} onChange={e=>setMenuText(e.target.value)} style={{ width:48, height:36, cursor:'pointer', border:'1px solid #555', borderRadius:4, background:'none' }}/>
        </Box>
      </Box>

      <Typography variant="subtitle2" sx={{ color:'#aaa', mb:1 }}>🔲 Прозорість елементів</Typography>
      {sliders.map(({label,val,fn}) => (
        <Box key={label} sx={{ mb:1.5 }}>
          <Typography variant="caption" sx={{ color:'#888' }}>{label}: {Math.round(val*100)}%</Typography>
          <input type="range" min="0.1" max="1" step="0.05" value={val}
            onChange={e=>fn(parseFloat(e.target.value))} style={{ width:'100%', accentColor:'var(--ac1, #7c4dff)' }}/>
        </Box>
      ))}

      <Box sx={{ display:'flex', gap:1, mt:2 }}>
        <Button variant="contained" onClick={handleApply} sx={{ bgcolor:'var(--ac1, #7c4dff)' }}>✅ Застосувати</Button>
        <Button variant="outlined" onClick={handleReset} sx={{ borderColor:'#555', color:'#aaa' }}>🔄 Скинути</Button>
      </Box>
    </Box>
  );
}
